import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import styles from './Orbitalfood.module.css';
import foodVideo from '../../../images/orbitalfood.mp4';

const OrbitalFoodProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [hoveredCapability, setHoveredCapability] = useState(null);
  const [hoveredProcess, setHoveredProcess] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Product & Service Content Architecture', icon: '💡' },
    { text: 'UX Content Flow Design', icon: '🎯' },
    { text: 'Technology & Process Translation', icon: '⚙️' },
    { text: 'Website Content Deployment', icon: '🌐' },
    { text: 'Value Proposition Messaging', icon: '📊' },
    { text: 'Brand & Visual Direction Alignment', icon: '🎨' },
    { text: 'Continuous Refinement', icon: '🚀' }
  ];

  const tools = [
    { name: 'CMS / Static Framework', icon: '📝', description: 'Content Blocks' },
    { name: 'Figma', icon: '🎨', description: 'UX Flow Mapping' },
    { name: 'Photoshop', icon: '🖼️', description: 'Visual Assets' },
    { name: 'Illustrator', icon: '✨', description: 'Icons & Diagrams' },
    { name: 'Analytics', icon: '📊', description: 'Engagement Review' }
  ];

  const skills = [
    'HTML5', 'CSS', 'Tailwind CSS', 'JavaScript', 'UX Architecture',
    'Product Positioning', 'Supply Chain Digitization', 'Content Strategy', 'Digital Transformation'
  ];


  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
        {/* Animated Background */}
        <div className={styles.backgroundGradient}>
          <div className={styles.gradientOrb1}></div>
          <div className={styles.gradientOrb2}></div>
          <div className={styles.gradientOrb3}></div>
        </div>

        <div className={styles.contentWrapper}>
          {/* Hero Section */}
          <section className={styles.heroSection}>
            <div className={styles.heroContent}>
              <div className={styles.heroLeft}>
                <div className={styles.projectBadge}>
                  <span className={styles.badgeDot}></span>
                  <span>Closed Project</span>
                </div>
                
                <h1 className={styles.projectTitle}>Food Brand Website</h1>
                <p className={styles.projectSubtitle}>Smart Food Supply & Distribution Platform</p>
                
                <div className={styles.projectMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Work</span>
                    <span className={styles.metaValue}>Website Content Structuring, UX Narrative, Product Positioning</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Year</span>
                    <span className={styles.metaValue}>2023 – Present</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Duration</span>
                    <span className={styles.metaValue}>Ongoing</span>
                  </div>
                </div>
                
                <p className={styles.heroDescription}>
                  This technology-enabled food supply and distribution platform designed to streamline sourcing, logistics, and delivery of quality food products. The platform focuses on operational efficiency, transparency, and scalability for businesses operating within the food ecosystem.
                </p>
                
              </div>
              
              <div className={styles.heroRight}>
                <div className={styles.heroVisual}>
                  <video 
                    className={styles.heroVideo}
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                  >
                    <source src={foodVideo} type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Introduction</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                A Food Brand Website designed to modernize how food products are presented and managed through a digital-first approach. By integrating structured content, streamlined workflows, and clear product visibility, the platform enhances brand communication and supports efficient coordination across sourcing and distribution processes. It focuses on delivering consistent quality, improving operational clarity, and creating a seamless experience for customers engaging with the brand online.
              </p>
            </div>
          </section>

          {/* My Role Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>My Role</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                My role involved shaping the Food Brand Website’s digital communication and product storytelling to effectively highlight its value. This included structuring content, defining how products and services are presented, and aligning business objectives with a user-friendly and visually engaging experience. I focused on translating operational capabilities into a clear, compelling digital presence that enhances brand perception and usability.
              </p>
            </div>
          </section>

          {/* Responsibilities Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Responsibilities</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.responsibilitiesGrid}>
              {responsibilities.map((responsibility, index) => (
                <div 
                  key={index}
                  className={styles.responsibilityCard}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setHoveredResponsibility(index)}
                  onMouseLeave={() => setHoveredResponsibility(null)}
                >
                  <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                  <div className={styles.cardIcon}>{responsibility.icon}</div>
                  <div className={styles.cardText}>{responsibility.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Tools Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Tools Used</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.toolsGrid}>
              {tools.map((tool, index) => (
                <div 
                  key={index}
                  className={styles.toolCard}
                  style={{ animationDelay: `${index * 0.15}s` }}
                  onMouseEnter={() => setHoveredTool(index)}
                  onMouseLeave={() => setHoveredTool(null)}
                >
                  <div className={styles.toolGlow}></div>
                  <div className={styles.toolIcon}>{tool.icon}</div>
                  <div className={styles.toolName}>{tool.name}</div>
                  <div className={styles.toolDescription}>{tool.description}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Skills Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.skillsContainer}>
              {skills.map((skill, index) => (
                <div 
                  key={index}
                  className={styles.skillTag}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <span className={styles.skillHash}>#</span>
                  {skill}
                </div>
              ))}
            </div>
          </section>

          

          

        </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
};

export default OrbitalFoodProjectShowcase;